import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu, X, Home, Users, Video, Gift, Wallet,
  FileText, LogOut, Search, Bell, ChevronDown, Building2,
  Coins, TrendingUp, GitMerge, MessageSquare, Settings,
  ShieldAlert, Activity, Power, ShieldCheck, ChevronLeft, ChevronRight, UserCircle, Newspaper,
  ArrowDownToLine, Key, Image as ImageIcon, Palette
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const token = sessionStorage.getItem('adminToken');
  const [unreadCount, setUnreadCount] = useState(0);

  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  React.useEffect(() => {
    if (!token) return;
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${constant.API_BASE_URL}/notifications/for-message`, {
          headers: { Authorization: token }
        });
        if (response.data.success) {
          const list = response.data.data || [];
          const unread = list.filter(n => n.notification_status === 'unread').length;
          setUnreadCount(unread);
        }
      } catch (err) {
        console.error("Error fetching unread notifications count:", err);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';
  const userName = userinfo?.user_name || 'Admin';

  const menuItems = [
    { path: '/security-logs', label: 'Security & Logs', icon: <ShieldAlert size={20} /> },
    { path: '/', label: 'Dashboard', icon: <Home size={20} /> },
    { path: '/organization', label: 'Organization Hub', icon: <Building2 size={20} /> },
    { path: '/news', label: 'News Management', icon: <Newspaper size={20} /> },
    { path: '/users', label: 'User Management', icon: <Users size={20} /> },
    { path: '/sellers', label: 'Seller Management', icon: <Users size={20} /> },
    { path: '/live-sessions', label: 'Room Management', icon: <Video size={20} /> },
    { path: '/banners', label: 'Banner Management', icon: <ImageIcon size={20} /> },
    { path: '/room-themes', label: 'Room Themes', icon: <Palette size={20} /> },
    { path: '/recharge', label: 'Recharge Management', icon: <Wallet size={20} /> },
    { path: '/wallet', label: 'Wallet Management', icon: <Wallet size={20} /> },
    { path: '/withdrawals', label: 'Withdrawal Requests', icon: <ArrowDownToLine size={20} /> },
    { path: '/coins', label: 'Coin Management', icon: <Coins size={20} /> },
    { path: '/targets-config', label: 'Target Config', icon: <TrendingUp size={20} /> },
    { path: '/level-settings', label: 'Level Settings (1-100)', icon: <TrendingUp size={20} /> },
    { path: '/store-management', label: 'Store Assets', icon: <Gift size={20} /> },
    { path: '/gifts', label: 'Gifts & Custom IDs', icon: <Gift size={20} /> },
    { path: '/rewards-tracking', label: 'Rewards Tracking', icon: <Gift size={20} /> },
    { path: '/commissions-tracking', label: 'Commissions', icon: <TrendingUp size={20} /> },
    { path: '/fraud-control', label: 'Fraud & Security', icon: <ShieldCheck size={20} /> },
    { path: '/reports', label: 'Reports Section', icon: <FileText size={20} /> },
    { path: '/support', label: 'Ticket & Support', icon: <MessageSquare size={20} /> },
    { path: '/policies', label: 'Policy Management', icon: <FileText size={20} /> },
    { path: '/settings', label: 'Settings Panel', icon: <Settings size={20} /> },
    { path: '/app-feedback', label: 'App Feedback', icon: <MessageSquare size={20} /> },
    { path: '/notifications', label: 'Notification System', icon: <Bell size={20} /> },
    { path: '/roles', label: 'Admin Role System', icon: <ShieldAlert size={20} /> },
    { path: '/permissions', label: 'Permission Management', icon: <Key size={20} /> },
    { path: '/system-monitor', label: 'System Monitor', icon: <Activity size={20} /> },
    { path: '/maintenance', label: 'Maintenance Mode', icon: <Power size={20} /> },
  ].filter(item => {
    if (userType === 'BD') {
      return item.path === '/' || item.path === '/organization' || item.path === '/agencies' || item.path === '/commissions-tracking';
    }
    if (userType === 'MANAGER') {
      return item.path === '/' || item.path === '/organization' || item.path === '/bd-management' || item.path === '/agencies' || item.path === '/commissions-tracking';
    }
    return true;
  });

  const searchResults = menuItems.filter(m => m.label.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/login');
  };

  const getPageTitle = (path) => {
    if (path === '/') return 'Dashboard';
    if (path.startsWith('/agencies/')) return 'Agency Details';
    if (path === '/agencies' || path === '/bd-management') return userType === 'BD' ? 'BD Management' : 'Agencies';
    if (path === '/users') return 'User Management';
    if (path === '/sellers') return 'Seller Management';
    if (path === '/live-sessions') return 'Room Management';
    if (path === '/recharge') return 'Recharge Management';
    if (path === '/wallet') return 'Wallet Management';
    if (path === '/withdrawals') return 'Withdrawal Requests';
    if (path === '/coins') return 'Coin Management';
    if (path === '/targets-config') return 'Target Config';
    if (path === '/level-settings') return 'Level Settings';
    if (path === '/store-management') return 'Store Assets';
    if (path === '/gifts') return 'Gifts & Custom IDs';
    if (path === '/security-logs') return 'Security & Logs';
    if (path === '/organization') return 'Organization Hub';
    if (path === '/settings') return 'Settings Panel';
    if (path === '/notifications') return 'Notifications';
    return path.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="flex h-screen bg-[#F4F7FE] font-sans overflow-hidden">
      {/* Sidebar - Desktop: Fixed, Mobile: Off-canvas sliding drawer */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 bg-white flex flex-col h-full
        transform transition-all duration-300 ease-in-out border-r border-gray-100 shadow-2xl lg:shadow-none
        ${isSidebarCollapsed ? 'lg:w-[88px]' : 'lg:w-[280px]'}
        w-[82vw] max-w-[280px]
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Collapse Button (Desktop Only) */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute -right-3 top-8 bg-white border border-gray-200 text-gray-500 rounded-full p-1 shadow-sm hover:text-pink-600 hidden lg:flex z-50 cursor-pointer"
          title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>

        {/* Brand Header */}
        <div className="h-16 md:h-20 flex items-center justify-between border-b border-gray-100 shrink-0 px-4">
          <div className="flex items-center gap-2.5">
            <img src="/xoxo-logo.jpeg" className="w-10 h-10 rounded-xl object-cover shadow-sm" alt="XOXO Logo" />
            {(!isSidebarCollapsed || isSidebarOpen) && (
              <div className="overflow-hidden">
                <span className="text-slate-800 text-base font-black tracking-tight block">XOXO Admin</span>
                <span className="text-[10px] text-pink-600 font-extrabold tracking-wider uppercase block">Control Panel</span>
              </div>
            )}
          </div>
          {/* Mobile Close Button */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden p-1.5 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition cursor-pointer"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 mt-2 flex-1 overflow-y-auto overscroll-contain scrollbar-thin">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                className={`
                  flex items-center px-3.5 py-3 rounded-xl transition-all duration-200 group
                  ${isSidebarCollapsed ? 'lg:justify-center' : 'gap-3.5'}
                  ${isActive
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-md shadow-pink-500/25'
                    : 'text-gray-600 hover:bg-pink-50 hover:text-pink-600'}
                `}
                title={item.label}
              >
                <span className={`${isActive ? 'text-white' : 'text-gray-400 group-hover:text-pink-500'} shrink-0`}>
                  {item.icon}
                </span>
                {(!isSidebarCollapsed || isSidebarOpen) && (
                  <span className="font-bold tracking-wide text-xs sm:text-sm whitespace-nowrap">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Mobile/Sidebar Bottom Account Card */}
        <div className="p-3 border-t border-gray-100 bg-slate-50/80 shrink-0">
          <div className="flex items-center justify-between p-2 rounded-xl bg-white border border-gray-100 shadow-xs">
            <div className="flex items-center gap-2.5 overflow-hidden">
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${userName}`} alt="Avatar" className="w-8 h-8 rounded-full bg-slate-100 border border-gray-200 shrink-0 object-cover" />
              {(!isSidebarCollapsed || isSidebarOpen) && (
                <div className="overflow-hidden">
                  <p className="text-xs font-bold text-slate-800 truncate">{userName}</p>
                  <p className="text-[10px] text-pink-600 font-extrabold uppercase truncate">{userType}</p>
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition shrink-0 cursor-pointer"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`flex-1 flex flex-col min-w-0 h-screen overflow-hidden transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-[88px]' : 'lg:ml-[280px]'}`}>

        {/* Top Header */}
        <header className="h-16 md:h-20 bg-white/80 backdrop-blur-xl sticky top-0 z-40 flex items-center justify-between px-3 sm:px-6 lg:px-8 border-b border-gray-200/60 shadow-xs shrink-0">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 text-slate-700 bg-slate-100 active:bg-slate-200 rounded-xl transition shrink-0 flex items-center justify-center cursor-pointer shadow-xs"
              aria-label="Toggle navigation"
            >
              <Menu size={20} />
            </button>
            <div className="flex flex-col min-w-0">
              <h2 className="text-base sm:text-xl lg:text-2xl font-black text-slate-800 truncate">
                {getPageTitle(location.pathname)}
              </h2>
              <p className="text-gray-400 text-xs hidden md:block">Welcome back, {userName} 👋</p>
            </div>
          </div>

          {/* Right Header Controls */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {/* Search Bar (Desktop) */}
            <div className="relative hidden md:flex items-center bg-slate-50 px-3.5 py-2 rounded-xl border border-gray-200 w-52 lg:w-64 focus-within:w-72 transition-all duration-300">
              <Search size={16} className="text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="Search menu..."
                className="bg-transparent border-none outline-none text-xs sm:text-sm ml-2.5 w-full text-gray-700 placeholder-gray-400"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                }}
                onFocus={() => setShowSearchResults(true)}
                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchResults.length > 0) {
                    navigate(searchResults[0].path);
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }
                }}
              />
              {showSearchResults && searchQuery && (
                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden z-50 max-h-60 overflow-y-auto">
                  {searchResults.length > 0 ? (
                    searchResults.map((result, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-3 hover:bg-pink-50 cursor-pointer flex items-center gap-3 text-sm text-gray-700 hover:text-pink-600 transition"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          navigate(result.path);
                          setSearchQuery('');
                          setShowSearchResults(false);
                        }}
                      >
                        {result.icon}
                        <span className="font-semibold">{result.label}</span>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-400 text-center">No matching section found.</div>
                  )}
                </div>
              )}
            </div>

            {/* Notification Bell */}
            <button
              onClick={() => navigate('/notifications')}
              className="relative p-2 sm:p-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl transition text-gray-600 cursor-pointer"
              title="Notifications"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center border-2 border-white animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Profile Avatar */}
            <div className="flex items-center gap-2.5 pl-2 sm:pl-3 border-l border-gray-200">
              <div className="text-right hidden lg:block">
                <p className="text-xs font-bold text-slate-800 leading-tight">{userName}</p>
                <p className="text-[10px] text-pink-600 font-extrabold uppercase">{userType}</p>
              </div>
              <div
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gradient-to-tr from-pink-500 to-purple-600 p-[2px] cursor-pointer hover:shadow-md transition shrink-0"
                onClick={() => navigate('/settings')}
                title="Profile & Settings"
              >
                <div className="h-full w-full rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                  <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${userName}`} alt="Profile" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Content Container */}
        <div className="flex-1 overflow-y-auto bg-[#F4F7FE] p-3 sm:p-5 lg:p-8 overscroll-contain">
          <div className="max-w-7xl mx-auto min-w-0">
            {children}
          </div>
        </div>
      </main>

      {/* Mobile Backdrop Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;